import React, { useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useSelector } from 'react-redux';
import { Helmet } from 'react-helmet';

// Services
import SystemDataService from '../../Services/SystemDataService';
import { getThankYouData } from '../../Public/thank-you-data.js';

// Components
import { ButtonPrimary, ButtonSecondary } from '../../Utils/Buttons/button-component';
import ImageComponenet from '../../Utils/Image/image-component';
import About from '../AboutUs-screren/AboutUs.js';
import Layout from '../../Utils/Navbar/navbar-layout-component.js';
import OurWorkSection from '../../Utils/Sections/section-our-work-component.js';

/* --- MAIN COMPONENT --- */
export default function ThankYou() {
    const navigate = useNavigate();

    // 1. System Data & Config
    const systemData = useSelector(s => s.systemData || {});
    const businessInfo = systemData.businessInfo || {};
    const { dir, language } = systemData;

    // 2. Load Localized Data
    const txt = getThankYouData(language);

    // 3. Get Logo
    const logoWhite = SystemDataService.byLabel(systemData.links, 'Logo') || {};

    // --- INITIALIZATION EFFECT ---
    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, []);

    const handleBackHome = (e) => {
        e.preventDefault();
        navigate('/');
    };

    const handleSeeProjects = (e) => {
        e.preventDefault();
        const el = document.getElementById('our-work');
        if (el) {
            el.scrollIntoView({ behavior: 'smooth' });
        }
    };

    return (
        <div dir={dir} className="flex flex-col w-full min-h-screen font-assistant bg-white animate-fadeIn">
            <Layout seo={txt.seo}>
                {/* ================= HERO SECTION ================= */}
                <div className="relative w-full min-h-[700px] pb-10 bg-slate-900 flex flex-col justify-center overflow-hidden">

                    {/* Background Layer with Overlay */}
                    <div className="absolute inset-0 w-full h-full">
                        <div
                            className="absolute inset-0 w-full h-full bg-cover bg-center transition-opacity duration-1000 opacity-40"
                            style={{ backgroundImage: `url(${txt.heroBg})` }}
                        />
                        <div className="absolute inset-0 bg-gradient-to-b from-slate-900/80 via-transparent to-white"></div>
                    </div>

                    <div className="relative container mx-auto px-6 lg:px-12 h-full flex flex-col lg:flex-row items-center z-10 pt-24 lg:pt-0">

                        {/* Text Content */}
                        <div className={`w-full lg:w-1/2 text-white space-y-8 mb-12 lg:mb-0 ${dir === 'rtl' ? 'lg:pl-16 text-right' : 'lg:pr-16 text-left'}`}>
                            {logoWhite.image && (
                                <div className="w-48 mb-8">
                                    <ImageComponenet
                                        src={logoWhite.image}
                                        alt="Logo"
                                        className="w-full h-auto object-contain brightness-0 invert"
                                        useSekeleton={false}
                                    />
                                </div>
                            )}
                            <h1 className="text-6xl md:text-7xl lg:text-8xl font-black uppercase er leading-[0.85]">
                                {txt.title}
                            </h1>
                            <p className="text-xl md:text-2xl text-white/70 font-medium max-w-lg leading-tight italic">
                                {txt.subtitle}
                            </p>
                        </div>

                        {/* Card Content: Integrated with Always On Air Rounding */}
                        <div className={`w-full lg:w-1/2 flex ${dir === 'rtl' ? 'justify-start' : 'justify-end'}`}>
                            <div className="w-full max-w-[460px] bg-white rounded-[3rem] shadow-2xl p-10 md:p-14 border border-slate-100">
                                <div className="flex flex-col gap-8">
                                    <div>
                                        <h3 className="text-3xl font-black text-slate-900 uppercase  mb-4">
                                            {txt.nextStepsTitle}
                                        </h3>
                                        <p className="text-lg text-slate-500 font-semibold leading-relaxed">
                                            {txt.nextStepsBody}
                                        </p>
                                    </div>

                                    <div className="space-y-6">
                                        <p className="text-xs text-blue-600 font-black uppercase tracking-[0.2em]">
                                            {txt.meanwhile}
                                        </p>
                                        <div className="flex flex-col gap-4">
                                            <ButtonPrimary
                                                onClick={handleSeeProjects}
                                                size="lg"
                                                className="w-full !rounded-full py-6 text-xl shadow-xl shadow-blue-500/20"
                                            >
                                                {txt.galleryBtn}
                                            </ButtonPrimary>
                                            <ButtonSecondary
                                                onClick={handleBackHome}
                                                size="lg"
                                                className="w-full !rounded-full py-6 text-xl border-slate-200"
                                            >
                                                {txt.homeBtn}
                                            </ButtonSecondary>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* ================= VALUE PROP SECTION ================= */}
                <About />

                {/* ================= DYNAMIC GALLERY SECTION ================= */}
                <OurWorkSection isMisionaryImage={false} />
            </Layout>
        </div>
    );
}